import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Check, X, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { Button } from "../components/ui/button";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export default function Trades() {
  const [trades, setTrades] = useState(null);
  const [tab, setTab] = useState("incoming");

  const load = () => api.get("/trades").then((res) => setTrades(res.data)).catch(() => setTrades([]));
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    try {
      const res = await api.post(`/trades/${id}/respond`, { action });
      if (action === "accept") {
        toast.success(res.data.other.contact_info
          ? `Accepted — contact them at: ${res.data.other.contact_info}`
          : "Accepted — they haven't added contact info yet");
      } else {
        toast.info("Trade ping declined");
      }
      load();
    } catch {
      toast.error("Couldn't respond. Try again.");
    }
  };

  const shown = (trades || []).filter((t) => t.direction === tab);

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 data-testid="trades-title" className="font-display text-4xl sm:text-5xl font-bold mb-2">Trades</h1>
        <p className="text-zinc-400 text-sm mb-4">Trade pings you've sent and received.</p>
        <p data-testid="trades-disclaimer" className="text-[11px] text-zinc-600 border-l-2 border-white/10 pl-3 mb-8 leading-relaxed">
          CardArk only connects collectors. Trades are arranged entirely between users, at your own risk —
          CardArk is not a party to, and accepts no responsibility for, any trade, item or payment.
        </p>

        <div className="flex gap-2 mb-6">
          <TabButton active={tab === "incoming"} onClick={() => setTab("incoming")} testId="trades-tab-incoming">
            <ArrowDownLeft className="w-3.5 h-3.5" /> Incoming
          </TabButton>
          <TabButton active={tab === "outgoing"} onClick={() => setTab("outgoing")} testId="trades-tab-outgoing">
            <ArrowUpRight className="w-3.5 h-3.5" /> Sent
          </TabButton>
        </div>

        {trades === null ? (
          <p className="text-zinc-600 text-sm animate-pulse">Loading...</p>
        ) : shown.length === 0 ? (
          <div data-testid="trades-empty" className="text-center py-16 text-zinc-600 text-sm">
            {tab === "incoming" ? "No trade pings received yet." : "You haven't pinged any trades yet — find cards in the "}
            {tab === "outgoing" && <Link to="/community" className="text-[#A78BFA] underline">Community</Link>}
          </div>
        ) : (
          <div className="space-y-4">
            {shown.map((t) => (
              <div key={t.id} data-testid={`trade-row-${t.id}`} className="border border-white/10 bg-[#111113] rounded-xl p-4 flex gap-4">
                <div className="w-14 shrink-0">
                  {t.card.image ? (
                    <img src={pcImageUrl(resolveUrl(t.card.image), "240")} onError={pcImageFallback} alt={t.card.name} className="w-14 aspect-[3/4] object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-14 aspect-[3/4] border border-white/10 rounded-lg" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{t.card.name}</p>
                  <p className="text-xs text-zinc-500 truncate">
                    {t.direction === "incoming" ? "From" : "To"}{" "}
                    <Link to={`/collector/${t.other.user_id}`} className="text-[#A78BFA] hover:underline">{t.other.display_name}</Link>
                    {" · "}{new Date(t.created_at).toLocaleDateString()}
                  </p>
                  {t.message && (
                    <p className="text-xs text-zinc-400 mt-1.5 flex items-start gap-1.5">
                      <MessageCircle className="w-3 h-3 mt-0.5 shrink-0" /> {t.message}
                    </p>
                  )}
                  {t.status === "accepted" && (
                    <div data-testid={`trade-contact-${t.id}`} className="mt-2 text-xs border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 rounded-lg px-3 py-2">
                      Accepted — contact {t.other.display_name}: <span className="font-semibold">{t.other.contact_info || "no contact info added"}</span>
                    </div>
                  )}
                  {t.status === "declined" && <p className="mt-2 text-xs text-zinc-600">Declined</p>}
                </div>
                {t.status === "pending" && t.direction === "incoming" && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button data-testid={`trade-accept-${t.id}`} size="sm" onClick={() => respond(t.id, "accept")} className="bg-emerald-600 hover:bg-emerald-500 rounded-full">
                      <Check className="w-3.5 h-3.5 mr-1" /> Accept
                    </Button>
                    <Button data-testid={`trade-decline-${t.id}`} size="sm" variant="outline" onClick={() => respond(t.id, "decline")} className="border-white/20 hover:bg-white/5 rounded-full">
                      <X className="w-3.5 h-3.5 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                {t.status === "pending" && t.direction === "outgoing" && (
                  <span className="text-xs text-[#F59E0B] shrink-0 self-start px-2 py-1 rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/10">Pending</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border transition-colors ${active ? "border-[#A78BFA] text-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}
    >
      {children}
    </button>
  );
}
